// lib/woFormat.mjs — R112: the ONE set of work-order wording + date/money
// helpers shared by the Maintenance board, Supplies & Expenses and the WO quick
// view (client/src/shared/lib/woFormat.ts re-exports this file, the way
// woSearch.mjs is shared). Pure ESM, no DOM, no I/O — `node lib/woFormat.test.mjs`
// runs the same code the browser bundles. Rules preserved from MaintenancePage:
//   · a bare 'YYYY-MM-DD' is a CALENDAR date pinned to UTC (parsing it with
//     new Date() alone shows the previous day in Pacific);
//   · "today" is the device's local calendar day (en-CA) unless a caller passes
//     as_of — never toISOString() (UTC flips the day at ~5pm Pacific);
//   · a reopened WO keeps its stale completed_at server-side, so "done at" is
//     only ever read through woDoneAt();
//   · the business window chips (this month / last month) are computed in
//     America/Los_Angeles so the page, the server and the owner portal agree.

export const BUSINESS_TZ = 'America/Los_Angeles';

// R110 #2 wording — payer (whose money) vs billed (has the owner been charged
// yet, and how). Stored values untouched: payer 'wch'|'owner';
// billing_status ''|'billed_owner'|'billed_os'.
export const WO_BILLED_LABEL = { billed_owner: 'billed · invoiced', billed_os: 'billed · on statement' };
export const WO_BILLED_TITLE = {
  billed_owner: 'Owner has been charged — invoiced directly',
  billed_os: 'Owner has been charged — added to their monthly owner statement',
};
export const WO_BILLED_OPTIONS = [
  ['', 'Not billed yet'],
  ['billed_owner', 'Billed · invoiced to owner'],
  ['billed_os', 'Billed · on owner statement'],
];
/** One-line explainer under Who pays / Billed yet? (no user data interpolated). */
export function woBillHintHtml(payer, billed) {
  if (!payer) return '<span>Pick who pays first.</span>';
  if (payer === 'owner') return '<span>The owner covers this — track here whether they\'ve been charged yet, and how.</span>';
  const clash = billed ? ' <span class="mn-wo-billclash">⚠ Billed, but WCH is marked as payer — check which is right.</span>' : '';
  return `<span>WCH covers this cost — there's nothing to bill the owner.</span>${clash}`;
}
export const payerLabel = (p) => (p === 'owner' ? 'owner pays' : p === 'wch' ? 'WCH pays' : '');

// The description the one-click "create a WO from this QuickBooks line" flow
// used to write — never owner-facing, never a "what was done".
export const PLACEHOLDER_RE = /^From the .* QuickBooks charge/i;
export const isPlaceholderDesc = (s) => PLACEHOLDER_RE.test(String(s || ''));

const BARE = /^\d{4}-\d{2}-\d{2}$/;
const pad2 = (n) => String(n).padStart(2, '0');

export function fmtDate(iso) {
  if (!iso) return '—';
  const bare = BARE.test(String(iso));
  const d = bare ? new Date(iso + 'T00:00:00Z') : new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', bare
    ? { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}
/** 'Oct 9' — a calendar day (the first 10 chars of anything date-like), pinned UTC. */
export function fmtDay(d) {
  if (!d) return '';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z');
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
export function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
/** USD; '—' when there's nothing to show (null, NaN, 0). */
export function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
export function fmtMins(m) {
  const v = Math.max(0, Math.round(Number(m) || 0));
  if (!v) return '0m';
  const h = Math.floor(v / 60), mm = v % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
}

/** The device's local calendar day (en-CA = YYYY-MM-DD), or the as_of a caller pins. */
export function todayLocal(asOf) {
  if (asOf && BARE.test(String(asOf))) return String(asOf);
  const d = asOf instanceof Date ? asOf : new Date();
  return d.toLocaleDateString('en-CA');
}
/** The device's calendar day of a timestamp; a bare YYYY-MM-DD is already a day. */
export function localDay(iso) {
  if (!iso) return '';
  const s = String(iso);
  if (BARE.test(s)) return s;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
}
/** Whole days from today to a YYYY-MM-DD (negative = past); null when unknown. */
export function daysFromToday(isoDate, today) {
  if (!isoDate || !BARE.test(String(isoDate).slice(0, 10))) return null;
  const t0 = new Date(todayLocal(today) + 'T00:00:00Z').getTime();
  const t1 = new Date(String(isoDate).slice(0, 10) + 'T00:00:00Z').getTime();
  if (isNaN(t1)) return null;
  return Math.round((t1 - t0) / 86400000);
}
/** When a WO was finished — only while it IS done (a reopened WO keeps a stale completed_at). */
export function woDoneAt(w) {
  if (!w || w.status !== 'done') return null;
  if (w.completed_at) return String(w.completed_at);
  const e = (Array.isArray(w.log) ? w.log : []).filter((x) => x && x.status === 'done').pop();
  return e && e.at ? String(e.at) : null;
}
/** The day the work is booked for: scheduled_date, else the window start. */
export function schedOf(w) {
  if (!w) return null;
  if (w.scheduled_date && /^\d{4}-\d{2}-\d{2}/.test(String(w.scheduled_date))) return String(w.scheduled_date).slice(0, 10);
  const st = w.scheduled_window && w.scheduled_window.start;
  return st && /^\d{4}-\d{2}-\d{2}/.test(String(st)) ? String(st).slice(0, 10) : null;
}
/** The window end when it is a real multi-day window (never before the start). */
export function schedEndOf(w) {
  const s = schedOf(w);
  if (!s || (w.scheduled_date && /^\d{4}-\d{2}-\d{2}/.test(String(w.scheduled_date)))) return null;
  const en = w.scheduled_window && w.scheduled_window.end;
  const e = en && /^\d{4}-\d{2}-\d{2}/.test(String(en)) ? String(en).slice(0, 10) : null;
  return e && e > s ? e : null;
}
/** { date, source } — 'checkout' tracks the listing's next checkout (effective_due), else the stored due_date. */
export function dueOf(w) {
  if (!w) return { date: null, source: null };
  if (w.due_source === 'checkout') return { date: w.effective_due ? String(w.effective_due).slice(0, 10) : null, source: 'checkout' };
  return { date: w.due_date ? String(w.due_date).slice(0, 10) : null, source: w.due_date ? 'due' : null };
}
export const statusLabel = (s) => (s === 'in_progress' ? 'In progress' : s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : 'Open');
export const priorityOf = (w) => (w && (w.priority ?? (w.type === 'pm' ? 'pm' : 'routine'))) || 'routine';
export const priorityLabel = (p) => ({ emergency: 'Emergency', urgent: 'Urgent', routine: 'Routine', pm: 'PM' }[p] || String(p || 'Routine'));

// ── business-timezone windows (R112 #2: the "last month" chip) ───────────────
/** YYYY-MM-DD in America/Los_Angeles for a moment (default: now). */
export function laToday(now) {
  const d = now instanceof Date ? now : new Date(now || Date.now());
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}
/** Inclusive from/to of a calendar month relative to LA-today: 0 = this month, -1 = last month. */
export function laMonthRange(offsetMonths = 0, now) {
  const [Y, M] = laToday(now).split('-').map(Number);
  const first = new Date(Date.UTC(Y, M - 1 + (Number(offsetMonths) || 0), 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0));
  const ymd = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return { from: ymd(first), to: ymd(last) };
}
/** from/to for a window chip: 'month' (this month, open-ended), 'last' (last month, both ends), or N days back (open-ended). */
export function windowRange(key, now) {
  if (key === 'month') return { from: laMonthRange(0, now).from, to: '' };
  if (key === 'last') return laMonthRange(-1, now);
  const n = Number(key);
  if (!Number.isFinite(n) || n <= 0) return { from: '', to: '' };
  const d = new Date(laToday(now) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - n);
  return { from: d.toISOString().slice(0, 10), to: '' };
}
